import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from "recharts";

const CHART_TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fafafa", fontWeight: 600 };
const CHART_ITEM_STYLE = { color: "#fafafa" };
const CHART_TICK = { fill: "#a1a1aa", fontSize: 12 };
const LEGEND_WRAPPER_STYLE = { fontSize: 12, color: "#a1a1aa" };

const formatMonth = (m) => {
  try { const [y, mo] = m.split("-"); return new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" }); }
  catch { return m; }
};

export function MonthlyPLChart({ monthlyPL }) {
  if (!monthlyPL?.length) return null;
  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Monthly P&L
        </CardTitle>
      </CardHeader>
      <CardContent data-testid="monthly-pl-chart">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={monthlyPL.map(m => ({ ...m, label: formatMonth(m.month) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" />
            <XAxis dataKey="label" tick={CHART_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={CHART_TICK} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} itemStyle={CHART_ITEM_STYLE}
              formatter={(v, name) => [`$${v.toLocaleString()}`, name.charAt(0).toUpperCase() + name.slice(1)]} />
            <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
            <Bar dataKey="revenue" name="Revenue" fill="#f59e0b" radius={[2, 2, 0, 0]} barSize={24} />
            <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[2, 2, 0, 0]} barSize={24} />
            <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[2, 2, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

export function RevenueTrendChart({ monthlyRevData }) {
  if (!monthlyRevData?.length) return null;
  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Revenue Trend
        </CardTitle>
      </CardHeader>
      <CardContent data-testid="revenue-trend-chart">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={monthlyRevData.map(m => ({ ...m, label: formatMonth(m.month) }))}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240 3.7% 15.9%)" />
            <XAxis dataKey="label" tick={CHART_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={CHART_TICK} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} itemStyle={CHART_ITEM_STYLE}
              formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
            <Area type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} fill="url(#revenueGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
